export enum CARD_CONNECT_CARD_CODES_NAMES {
  MC = 'MasterCard',
  VISA = 'Visa',
  AMEX = 'American Express',
  DISC = 'Discover',
  DNR = "Diner's Club",
  JCB = 'Japan Credit Bureau',
  UP = 'Union Pay',
  MAES = 'Maestro',
  ECHK = 'ECHK',
  ESAV = 'ESAV',
  PBEBIT = 'PBEBIT',
}

export class CardType {
  constructor(public name: string, public isBank: boolean) {}

  static readonly ECHK = new CardType(CARD_CONNECT_CARD_CODES_NAMES.ECHK, true);
  static readonly ESAV = new CardType(CARD_CONNECT_CARD_CODES_NAMES.ESAV, true);
  static readonly PBEBIT = new CardType(CARD_CONNECT_CARD_CODES_NAMES.PBEBIT, true);
  static readonly VISA = new CardType(CARD_CONNECT_CARD_CODES_NAMES.VISA, false);
  static readonly MC = new CardType(CARD_CONNECT_CARD_CODES_NAMES.MC, false);
  static readonly AMEX = new CardType(CARD_CONNECT_CARD_CODES_NAMES.AMEX, false);
  static readonly DISC = new CardType(CARD_CONNECT_CARD_CODES_NAMES.DISC, false);
  static readonly DNR = new CardType(CARD_CONNECT_CARD_CODES_NAMES.DNR, false);
  static readonly JCB = new CardType(CARD_CONNECT_CARD_CODES_NAMES.JCB, false);
  static readonly UP = new CardType(CARD_CONNECT_CARD_CODES_NAMES.UP, false);
  static readonly MAES = new CardType(CARD_CONNECT_CARD_CODES_NAMES.MAES, false);
}

export const getCardConnectCardName = (accttype: string): string => {
  return (CardType as any)[accttype]?.name || CardType.DNR.name;
};

export const getCardConnectCard = (accttype: string): CardType => {
  return (CardType as any)[accttype] || CardType.DNR;
};

// Credit card helpers: detection, formatting, validation

export interface CardTypeDef {
  type: string;
  pattern: RegExp;
  gaps: number[];
  lengths: number[];
  code: { name: string; size: number };
}

export const CARD_TYPES: CardTypeDef[] = [
  {
    type: 'visa',
    pattern: /^4/,
    gaps: [4, 8, 12],
    lengths: [16, 18, 19],
    code: { name: 'CVV', size: 3 },
  },
  {
    type: 'mastercard',
    pattern: /^5[1-5]|^2[2-7]/,
    gaps: [4, 8, 12],
    lengths: [16],
    code: { name: 'CVC', size: 3 },
  },
  {
    type: 'american-express',
    pattern: /^3[47]/,
    gaps: [4, 10],
    lengths: [15],
    code: { name: 'CID', size: 4 },
  },
  {
    type: 'discover',
    pattern: /^6011|^65|^64[4-9]/,
    gaps: [4, 8, 12],
    lengths: [16],
    code: { name: 'CID', size: 3 },
  },
];

export const detectCardType = (number: string) => {
  const cleanNumber = (number || '').replace(/\D/g, '');
  return CARD_TYPES.find((card) => card.pattern.test(cleanNumber)) || null;
};

export const isValidCardNumber = (number: string): boolean => {
  const cleanNumber = (number || '').replace(/\D/g, '');
  const invalidDigits = /[^0-9- ]/;
  if (invalidDigits.test(number)) return false;
  if (!cleanNumber || cleanNumber.length < 14 || cleanNumber.length > 16) return false;
  const computed = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
  let sum = 0;
  let digit = 0;
  let i = cleanNumber.length;
  let even = true;
  while (i--) {
    digit = Number(cleanNumber[i]);
    sum += (even = !even) ? computed[digit] : digit;
  }
  return sum > 0 && sum % 10 === 0;
};

export const isValidExpDate = (expDate: string): boolean => {
  if (!/^\d{2}\/\d{4}$/.test(expDate)) return false;
  const [month, year] = expDate.split('/');
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  if (monthNum < 1 || monthNum > 12) return false;
  if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) return false;
  return true;
};

export const isValidCvv = (cvv: string, cardNumber: string): boolean => {
  const cleanCardNumber = (cardNumber || '').replace(/[^\d]/g, '');
  let expectedLength = 3;
  if (/^3[47]/.test(cleanCardNumber)) {
    expectedLength = 4;
  }
  return new RegExp(`^[0-9]{${expectedLength}}$`).test(cvv || '');
};

export const formatCardNumber = (value: string): string => {
  const digitsOnly = (value || '').replace(/\D/g, '');
  const detectedType = detectCardType(digitsOnly);
  const maxDigits = detectedType ? Math.max(...detectedType.lengths) : 16;
  const truncated = digitsOnly.slice(0, maxDigits);
  if (!detectedType) {
    return truncated.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  }
  let formatted = '';
  for (let i = 0; i < truncated.length; i++) {
    if (detectedType.gaps.includes(i) && i > 0) {
      formatted += ' ';
    }
    formatted += truncated[i];
  }
  return formatted;
};

export const formatExpDate = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  const month = digits.slice(0, 2);
  const year = digits.slice(2);
  return `${month}/${year}`;
};


